// schema
import * as ISchema from "#webapp/src/__schema__";
// libraries
import React from "react";

// components
import { WebSettingsContext } from "../web-settings";

import { TeamPicker } from "#webapp/src/components/team-picker";

// util
import { track, TrackingEvents } from "#webapp/src/util/analytics.util";
import { logger } from "#webapp/src/util/logger.util";
import { maybeGetPrimaryTeamFromOrg } from "#webapp/src/util/team.util";

// mutations
import { setPrimaryTeam } from "#webapp/src/mutations";

// state
import { getCurrentEnvironment } from "#webapp/src/state/relay-environment";

// styles
import { greys } from "@clockwise/web-commons/src/styles/color.styles";

import { A } from "#webapp/src/components/ui";

// material-ui
import { Button, Typography } from "@clockwise/design-system";
import { Dialog, DialogActions, DialogContent, DialogTitle } from "@material-ui/core";
import toast from "react-hot-toast";

// ~-~-~-~-~-~-~-
// Primary Team Select
// ~-~-~-~-~-~-~-
interface PrimaryTeamSelectProps {
  org: ISchema.IOrg;
}

export const PrimaryTeamSelect = (props: PrimaryTeamSelectProps) => {
  const { webSettingsRefetch } = React.useContext(WebSettingsContext);

  const maybePrimaryTeam = maybeGetPrimaryTeamFromOrg(props.org.primaryTeam);
  const primaryTeamId = maybePrimaryTeam ? maybePrimaryTeam.teamId : "";

  const [showPrimaryTeamModal, setShowPrimaryTeamModal] = React.useState(false);
  const [selectedPrimaryTeamId, setSelectedPrimaryTeamId] = React.useState(primaryTeamId);

  // ~-~-~-~-~-~-~-
  // Helpers
  // ~-~-~-~-~-~-~-
  const onClickSetPrimaryTeam = () => {
    setShowPrimaryTeamModal(true);
    track(TrackingEvents.TEAMS.CLICKED_CHANGE_PRIMARY_TEAM);
  };

  const onSetPrimaryTeamModalClose = () => {
    setShowPrimaryTeamModal(false);
    track(TrackingEvents.TEAMS.CLOSE_CHANGE_PRIMARY_TEAM_MODAL);
  };

  const savePrimaryTeam = () => {
    const prevPrimaryTeamId =
      props.org.primaryTeam?.__typename !== "GraphEntityError" && props.org.primaryTeam.teamId;

    if (!selectedPrimaryTeamId) {
      return logger.error("Cannot save empty team id");
    }

    setPrimaryTeam(
      getCurrentEnvironment(),
      { orgRelayId: props.org.id, teamId: selectedPrimaryTeamId },
      () => {
        toast.success("Set primary team");
        track(TrackingEvents.TEAMS.CHANGED_PRIMARY_TEAM, {
          selectedPrimaryTeamId,
          prevPrimaryTeamId,
        });
        webSettingsRefetch();
        setShowPrimaryTeamModal(false);
      },
      () => {
        toast.error("Failed to set primary team");
        logger.error("Failed to set primary team");
      },
    );
  };

  // ~-~-~-~-~-~-~-
  // Render
  // ~-~-~-~-~-~-~-
  if (props.org.userTeams.__typename === "GraphEntityError") {
    return <></>;
  }

  const maybeRenderSetPrimaryTeamModal = (
    userTeams?: ISchema.ITeamList,
    primaryTeamId?: string,
  ) => {
    if (!userTeams) {
      return "";
    }

    return (
      <Dialog
        open={showPrimaryTeamModal}
        onClose={onSetPrimaryTeamModalClose}
        PaperProps={{ style: { maxWidth: 700, width: 700 } }}
      >
        <DialogTitle>
          <Typography variant="h3" className="cw-mb-4">
            Set your primary team
          </Typography>
        </DialogTitle>
        <DialogContent style={{ paddingTop: 0 }}>
          <Typography
            style={{
              fontSize: 15,
              lineHeight: 1.8,
              color: greys.slate,
              marginBottom: 30,
            }}
          >
            Your primary team is your default team in Clockwise. You will receive proactive alerts
            about your primary teammates' availability and capacity.
          </Typography>
          <TeamPicker
            userTeams={userTeams}
            teamId={primaryTeamId}
            onChange={setSelectedPrimaryTeamId}
          />
        </DialogContent>
        <DialogActions>
          <Button
            // keyboardFocused={true}
            variant="text"
            onClick={onSetPrimaryTeamModalClose}
          >
            Cancel
          </Button>
          <Button variant="text" onClick={savePrimaryTeam} color="primary">
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  return (
    <>
      {primaryTeamId ? (
        <A onClick={onClickSetPrimaryTeam} style={{ fontSize: 14 }}>
          Change primary team
        </A>
      ) : (
        ""
      )}
      {maybeRenderSetPrimaryTeamModal(props.org.userTeams, primaryTeamId)}
    </>
  );
};
